import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControl,
  Autocomplete,
} from "@mui/material";

import { AdditionalItem } from "types";
import CustomNumberInput from "components/CustomNumberInput";
import { useSession } from "providers/SessionProvider";
import { getUserFullName } from "helpers";

type Props = {
  open: boolean;
  handleOpen: () => void;
  value: AdditionalItem;
  onEdit: (additionalItem: AdditionalItem) => void;
};

export default function EditAdditionalItemModal({
  open = true,
  handleOpen,
  value,
  onEdit,
}: Props) {
  const { clients, users } = useSession();
  const [data, setData] = useState<AdditionalItem>(value);

  const [selectedClient, setSelectedClient] = useState<{
    id: number;
    label: string;
  }>({
    id: 0,
    label: "Todos",
  });

  useEffect(() => {
    if (open) {
      setData(value);
      const client = clients.find((c) => c.id === value.client_id);
      setSelectedClient({
        id: client ? client.id : 0,
        label: client ? `${client.code} - ${client.razon_social}` : "Todos",
      });
    }
  }, [open, value]);

  const options = useMemo(() => {
    const clientList = clients.map((client) => ({
      id: client.id,
      label: `${client.code} - ${client.razon_social}`,
    }));
    clientList.unshift({ id: 0, label: "Todos" });
    return clientList;
  }, [clients]);

  const handleChange = <K extends keyof Omit<AdditionalItem, "id">>(
    name: K,
    value: Omit<AdditionalItem, "id">[K],
  ) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleNumberChange = <K extends keyof Omit<AdditionalItem, "id">>(
    name: K,
    rawValue: string,
  ) => {
    setData((prev) => ({
      ...prev,
      [name]: rawValue,
    }));
  };

  const onSubmit = (updatedAdditionalItem: AdditionalItem) => {
    onEdit({
      ...updatedAdditionalItem,
      client:
        clients.find((c) => c.id === updatedAdditionalItem.client_id) || null,
    });
    handleOpen();
  };

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "30rem" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant="h5" gutterBottom>
            Editar ítem adicional
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              id="outlined-required"
              label="Descripción"
              fullWidth
              value={data.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth margin="none">
              <Autocomplete
                id="client"
                autoComplete
                value={selectedClient}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={options}
                onChange={(
                  e,
                  value: {
                    id: number;
                    label: string;
                  } | null,
                ) => {
                  if (value?.label === "Todos") {
                    handleChange("client_id", null);
                  } else {
                    handleChange("client_id", value?.id || null);
                  }
                  setSelectedClient(value || { id: 0, label: "Todos" });
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} required label="Cliente" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <CustomNumberInput
              label="Precio"
              required
              value={data.price.toString()}
              onChange={(e) => handleNumberChange("price", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required margin="none">
              <CustomNumberInput
                label="Tiempo (Minutos)"
                value={
                  data.time_minutes ? data.time_minutes.toString() : undefined
                }
                allowOnlyPositive
                onChange={(e) =>
                  handleNumberChange("time_minutes", e.target.value)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Creado por"
              value={
                value.created_by
                  ? getUserFullName(value.created_by, users)
                  : "SISTEMA"
              }
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              onClick={() => {
                onSubmit(data);
              }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
